import { useAppSelector } from '@store';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { fetchAutosuggest } from 'src/services/routes';
import {
  defineAccountId,
  defineDestination,
  defineOrigin,
  defineRouteName,
  defineStops
} from '../../../store/routes/RoutesSlice';
import { EOperationType, ICsvData } from '../../trips/types';
import { formatStopTime } from '@utils';

const fetchAutosuggestText = async (text: string) =>
  fetchAutosuggest(text)
    .then(r =>
      Array.isArray(r) && r[0]
        ? {
          label: r[0]?.title || '',
          description: r[0]?.address?.label || '',
          ...r[0]
        }
        : null
    )
    .catch(e => {
      console.error('Erro:', e);
      return null;
    });

const fetchDataAutosuggestTextOrPosition = async (item: ICsvData) =>
  (item.position && await fetchAutosuggestText(item.position)) ||
  ((item.address || item.city || item.state) &&
    await fetchAutosuggestText(`${item.address}, ${item.city} - ${item.state}, ${item.zipCode}`));

const generateRouteIdFromCsv = (csvData: ICsvData[]): string => {
  const origem = csvData.find(item => item.operationType === EOperationType.ORIGIN);
  const destino = csvData.find(item => item.operationType === EOperationType.DESTINATION);

  if (origem && destino) {
    const timestamp = Date.now();
    const origemCode = origem.city?.substring(0, 3).toUpperCase() || 'ORG';
    const destinoCode = destino.city?.substring(0, 3).toUpperCase() || 'DST';
    return `CSV_${origemCode}_${destinoCode}_${timestamp}`;
  }
  return `CSV_ROUTE_${Date.now()}`;
};

const generateRouteNameFromCsv = (csvData: ICsvData[]): string => {
  const origem = csvData.find(item => item.operationType === EOperationType.ORIGIN);
  const destino = csvData.find(item => item.operationType === EOperationType.DESTINATION);

  if (origem && destino) {
    return `Rota CSV: ${origem.city} → ${destino.city}`;
  }

  return `Rota CSV - ${new Date().toLocaleDateString()}`;
};

const useFromFileData = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const profile = useAppSelector(state => state.login.userProfile);
  const [csvData, setCsvData] = useState<any>();

  useEffect(() => {
    if (location.state?.csvData) {
      setCsvData(location.state.csvData);
    }
  }, [location.state]);

  useEffect(() => {
    if (!csvData || !Array.isArray(csvData)) return;
    processCsvData();
  }, [csvData]);

  const processCsvData = async () => {
    const firstItem = csvData[0];

    dispatch(defineAccountId(firstItem?.company ? (profile?.account as string) : 'CSV_ACCOUNT'));
    dispatch(defineRouteName(generateRouteNameFromCsv(csvData)));

    const routeId = generateRouteIdFromCsv(csvData);

    const origem = csvData.find((item: ICsvData) => item.operationType === EOperationType.ORIGIN);
    if (origem) {
      const suggestion = await fetchDataAutosuggestTextOrPosition(origem);
      if (suggestion) {
        dispatch(defineOrigin({ ...suggestion.position, ...suggestion }));
      }
    }

    const destino = csvData.find((item: ICsvData) => item.operationType === EOperationType.DESTINATION);
    if (destino) {
      const suggestion = await fetchDataAutosuggestTextOrPosition(destino);
      if (suggestion) {
        dispatch(defineDestination({ ...suggestion.position, ...suggestion }));
      }
    }

    const buildStops = async (items: ICsvData[], category: string) => {
      const result = [];
      for (let i = 0; i < items.length; i++) {
        const suggestion = await fetchDataAutosuggestTextOrPosition(items[i]);
        if (suggestion) {
          result.push({
            position: {
              ...suggestion.position,
              label: suggestion.label,
            },
            stayTime: formatStopTime(items[i].stopTime),
            category,
            order: i + 1,
          });
        }
      }
      return result;
    };

    const intermediateStops = csvData.filter(
      (item: ICsvData) =>
        item.operationType !== EOperationType.ORIGIN &&
        item.operationType !== EOperationType.DESTINATION &&
        item.operationType !== EOperationType.UNLOADING
    );
    const unloadingStops = csvData.filter((item: ICsvData) => item.operationType === EOperationType.UNLOADING);

    const stops = await buildStops(intermediateStops, 'LOADING');
    const unloads = await buildStops(unloadingStops, 'UNLOADING');

    const allStops = [...stops, ...unloads];
    if (allStops.length > 0) {
      dispatch(defineStops(allStops));
    }
  };

  return { csvData };
};

export default useFromFileData;
